/* 
 * Global JavaScript for Portofino Flooring Theme
 * Handles interactive elements and theme functionality
 */

class ThemeGlobal {
  constructor() {
    this.init();
  }

  init() {
    try {
      this.initMobileMenu();
      this.initSearch();
      this.initCartDrawer();
      this.initScrollToTop();
      this.initLazyLoading();
      console.log('🎯 Portofino Theme cargado correctamente');
    } catch (error) {
      console.error('Error inicializando tema:', error);
    }
  }

  // Mobile Menu Toggle
  initMobileMenu() {
    try {
      const mobileToggle = document.querySelector('.header__mobile-toggle');
      const mobileMenu = document.querySelector('#mobile-menu');
      
      if (mobileToggle && mobileMenu) {
        mobileToggle.addEventListener('click', () => {
          const isOpen = !mobileMenu.hasAttribute('hidden');
          
          if (isOpen) {
            mobileMenu.setAttribute('hidden', '');
            mobileToggle.setAttribute('aria-expanded', 'false');
            document.body.style.overflow = '';
          } else {
            mobileMenu.removeAttribute('hidden');
            mobileToggle.setAttribute('aria-expanded', 'true');
            document.body.style.overflow = 'hidden';
          }
        });

        // Close on escape key
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && !mobileMenu.hasAttribute('hidden')) {
            mobileMenu.setAttribute('hidden', '');
            mobileToggle.setAttribute('aria-expanded', 'false');
            document.body.style.overflow = '';
          }
        });

        // Close on window resize
        window.addEventListener('resize', () => {
          if (window.innerWidth >= 768 && !mobileMenu.hasAttribute('hidden')) {
            mobileMenu.setAttribute('hidden', '');
            mobileToggle.setAttribute('aria-expanded', 'false');
            document.body.style.overflow = '';
          }
        });
      }
    } catch (error) {
      console.error('Error inicializando menú móvil:', error);
    }
  }

  // Search Functionality
  initSearch() {
    try {
      const searchToggle = document.querySelector('.header__search-toggle');
      const searchDrawer = document.querySelector('#search-drawer');
      const searchInput = document.querySelector('.search-input');
      
      if (searchToggle && searchDrawer) {
        searchToggle.addEventListener('click', () => {
          const isOpen = !searchDrawer.hasAttribute('hidden');
          
          if (isOpen) {
            searchDrawer.setAttribute('hidden', '');
            searchToggle.setAttribute('aria-expanded', 'false');
          } else {
            searchDrawer.removeAttribute('hidden');
            searchToggle.setAttribute('aria-expanded', 'true');
            setTimeout(() => {
              if (searchInput) searchInput.focus();
            }, 100);
          }
        });

        // Close search on escape
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && !searchDrawer.hasAttribute('hidden')) {
            searchDrawer.setAttribute('hidden', '');
            searchToggle.setAttribute('aria-expanded', 'false');
          }
        });
      }
      console.log('✅ SearchForm definido correctamente');
    } catch (error) {
      console.error('Error inicializando búsqueda:', error);
    }
  }

  // Cart Drawer (basic functionality)
  initCartDrawer() {
    try {
      const cartIcon = document.querySelector('#cart-icon-bubble');
      
      if (cartIcon) {
        // Update cart count on page load
        this.updateCartCount();
        
        // Listen for cart updates
        document.addEventListener('cart:updated', () => {
          this.updateCartCount();
        });
      }
    } catch (error) {
      console.error('Error inicializando carrito:', error);
    }
  }

  updateCartCount() {
    try {
      fetch('/cart.json')
        .then(response => response.json())
        .then(cart => {
          const cartCount = document.querySelector('.header__cart-count');
          const cartIcon = document.querySelector('#cart-icon-bubble');
        
          if (cartCount) {
            cartCount.textContent = cart.item_count;
            cartCount.style.display = cart.item_count > 0 ? 'block' : 'none';
          }
          
          if (cartIcon) {
            cartIcon.setAttribute('aria-label', `Cart (${cart.item_count})`);
          }
        })
        .catch(error => console.error('Error updating cart:', error));
    } catch (error) {
      console.error('Error en updateCartCount:', error);
    }
  }

  // Scroll to Top Button
  initScrollToTop() {
    try {
      const scrollButton = document.createElement('button');
      scrollButton.className = 'scroll-to-top';
      scrollButton.innerHTML = '↑';
      scrollButton.setAttribute('aria-label', 'Scroll to top');
      scrollButton.style.cssText = `
        position: fixed;
        bottom: 2rem;
        right: 2rem;
        width: 50px;
        height: 50px;
        background: var(--color-primary);
        color: white;
        border: none;
        border-radius: 50%;
        font-size: 1.2rem;
        cursor: pointer;
        opacity: 0;
        visibility: hidden;
        transition: all 0.3s ease;
        z-index: 1000;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      `;

      document.body.appendChild(scrollButton);

      // Show/hide button based on scroll position
      window.addEventListener('scroll', () => {
        if (window.scrollY > 300) {
          scrollButton.style.opacity = '1';
          scrollButton.style.visibility = 'visible';
        } else {
          scrollButton.style.opacity = '0';
          scrollButton.style.visibility = 'hidden';
        }
      });

      // Scroll to top on click
      scrollButton.addEventListener('click', () => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      });
    } catch (error) {
      console.error('Error inicializando scroll to top:', error);
    }
  }

  // Lazy Loading for Images
  initLazyLoading() {
    try {
      if ('IntersectionObserver' in window) {
        const imageObserver = new IntersectionObserver((entries, observer) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const img = entry.target;
              img.src = img.dataset.src;
              img.classList.remove('lazy');
              observer.unobserve(img);
            }
          });
        });

        document.querySelectorAll('img[data-src]').forEach(img => {
          imageObserver.observe(img);
        });
      }
      console.log('✅ Búsqueda predictiva inicializada');
    } catch (error) {
      console.error('Error inicializando lazy loading:', error);
    }
  }
}

// Form Validation Helper
class FormValidator {
  static validateEmail(email) {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  static validatePhone(phone) {
    const re = /^[\+]?[1-9][\d]{0,15}$/;
    return re.test(phone.replace(/\s/g, ''));
  }

  static addErrorClass(element, message) {
    element.classList.add('field-error');
    const errorElement = element.parentNode.querySelector('.field-error-message');
    if (errorElement) {
      errorElement.textContent = message;
    } else {
      const errorMsg = document.createElement('span');
      errorMsg.className = 'field-error-message';
      errorMsg.textContent = message;
      errorMsg.style.cssText = 'color: var(--color-accent); font-size: 0.85rem; margin-top: 0.25rem; display: block;';
      element.parentNode.appendChild(errorMsg);
    }
  }

  static removeErrorClass(element) {
    element.classList.remove('field-error');
    const errorElement = element.parentNode.querySelector('.field-error-message');
    if (errorElement) {
      errorElement.remove();
    }
  }
}

// Utility Functions
const Utils = {
  debounce(func, wait, immediate) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        timeout = null;
        if (!immediate) func(...args);
      };
      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) func(...args);
    };
  },

  throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  },

  formatMoney(cents, format = '${{amount}}') {
    const value = (cents / 100).toFixed(2);
    return format.replace('{{amount}}', value);
  }
};

// Initialize theme when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  console.log('Portofino Theme loaded');
  new ThemeGlobal();
  console.log('Portofino Theme DOM ready');
});

// Export for other scripts
window.ThemeGlobal = ThemeGlobal;
window.FormValidator = FormValidator;
window.Utils = Utils;
